<template>
  <Transition name="drawer">
    <div
      v-if="modelValue"
      class="drawer-mask"
      role="dialog"
      aria-modal="true"
      @click.self="close"
    >
      <div
        ref="containerRef"
        class="drawer-container"
        :class="{ 'is-dragging': isDragging }"
        :style="containerStyle"
        @click.stop
      >
        <div
          class="drawer-handle"
          aria-hidden="true"
          @pointerdown="onPointerDown"
        />
        <slot />
      </div>
    </div>
  </Transition>
</template>

<script>
import { ref, computed, watch } from 'vue';
import { useFocusTrap } from '@/composables/useFocusTrap';

const CLOSE_THRESHOLD_PX = 100;
const FAST_SWIPE_MS = 250;
const FAST_SWIPE_MIN_PX = 40;

export default {
  name: 'Drawer',
  props: {
    modelValue: {
      type: Boolean,
      default: false,
    },
  },
  emits: ['update:modelValue'],
  setup(props, { emit }) {
    const containerRef = ref(null);
    const isOpen = computed(() => props.modelValue);
    useFocusTrap(containerRef, isOpen);
    const dragOffset = ref(0);
    const isDragging = ref(false);
    const startY = ref(0);
    const startTime = ref(0);

    const containerStyle = computed(() => ({
      transform: dragOffset.value > 0 ? `translateY(${dragOffset.value}px)` : undefined,
    }));

    function close() {
      emit('update:modelValue', false);
    }

    function onPointerDown(e) {
      if (e.pointerType === 'mouse' && e.button !== 0) return;
      isDragging.value = true;
      startY.value = e.clientY;
      startTime.value = Date.now();
      dragOffset.value = 0;
      document.addEventListener('pointermove', onPointerMove, { passive: false });
      document.addEventListener('pointerup', onPointerUp);
      document.addEventListener('pointercancel', onPointerUp);
    }

    function onPointerMove(e) {
      if (!isDragging.value) return;
      e.preventDefault();
      const dy = e.clientY - startY.value;
      dragOffset.value = Math.max(0, dy);
    }

    function onPointerUp() {
      if (!isDragging.value) return;
      document.removeEventListener('pointermove', onPointerMove);
      document.removeEventListener('pointerup', onPointerUp);
      document.removeEventListener('pointercancel', onPointerUp);
      const totalTime = Date.now() - startTime.value;
      const shouldClose =
        dragOffset.value > CLOSE_THRESHOLD_PX ||
        (dragOffset.value > FAST_SWIPE_MIN_PX && totalTime < FAST_SWIPE_MS);
      isDragging.value = false;
      dragOffset.value = 0;
      if (shouldClose) {
        close();
      }
    }

    watch(
      () => props.modelValue,
      (open) => {
        if (!open) {
          isDragging.value = false;
          dragOffset.value = 0;
        }
      }
    );

    return {
      containerRef,
      isDragging,
      containerStyle,
      close,
      onPointerDown,
    };
  },
};
</script>

<style scoped>
.drawer-handle {
  user-select: none;
}
</style>
